// CompanyRelationsSection.tsx — Verknüpfungen der FIRMEN-Anlage, inline:
//   (1) Verantwortung fürs Unternehmen — Listen „Unternehmensleitung und
//       Gesellschafter", Verantwortung, Mensch im Unternehmen (28.09.2026)
//   (2) Verbindungen zu Firmen            (3) Kundenverbindung
// Voll über formFields gesteuert (kein Doppel-State) — jede Kategorie als
// eigener JSON-Key (rel_gfg/rel_verant/rel_firmaConn/rel_kvGroupId). Persistiert
// wird NICHTS hier; das passiert beim Speichern via persistCollectedRelations.

import * as React from "react";
import { SectionCard } from "../forms/formPrimitives";
import { MenschenFuersUnternehmenList, type MenschEntry, type MenschenSteuerung } from "./MenschenFuersUnternehmenList";
import { FirmaConnList, type FirmaConnSteuerung } from "./FirmaConnList";
import type { TabellenAblage } from "../forms/BeziehungsTabelle";
import {
  ABLAGE_HINWEIS,
  ZIEH_ART_FIRMENAKTE,
  ablageFuer,
  pruefeAblage,
  ziehDatenVon,
  ziehMarkerVon,
  ziehQuelleAus,
  type FirmenListe,
  type ZiehQuelle,
} from "./beziehungsZiehen";
import { KundenverbindungSelect } from "./KundenverbindungSelect";
import type { FirmaConnItem } from "./types";
import { useUnternehmensBegriff, verantwortungTitel } from "../../utils/unternehmensBegriff";
import { AnlageOrganigramm, DiagrammKnopf, type AnlageBeziehungen } from "./AnlageDiagramme";
import type { BeziehungsZeile } from "../forms/BeziehungsTabelle";
import { refName, zeilenAnzeigeVon } from "./beziehungsAnzeige";
import { useVerantwortlichkeiten } from "../verantwortlichkeit/useVerantwortlichkeiten";
import { baueKategorieVon, gruppenVon } from "./menschenGruppen";
import { BETEILIGUNG, MUTTERFIRMA, istFremdeBeteiligung } from "../../data/firmenverbindungen";
import { eintragAnteile } from "../../data/anteilsbeziehung";


const parseArr = (raw: unknown): any[] => {
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
};
const str = (v: unknown): string => (typeof v === "string" ? v : "");
/** Rechtsform-FK aus dem Formular — `LegalFormSelect` legt ihn als String ab. */
const zahl = (v: unknown): number | undefined => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

/** Zieh-Quelle einer Firmenverbindung. */
const firmaQuelle = (it: FirmaConnItem, liste: FirmenListe): ZiehQuelle => ({
  liste,
  eintrag: "firmaConn",
  id: it.id,
  gegenueber: "Firma",
  role: it.role,
  share: it.share,
});

export function CompanyRelationsSection({
  formFields,
  updateField,
  existingMenschen,
  existingFirmaConn,
  existingKv,
  firmaId,
}: {
  /** Id der Firma in der Akte (Bearbeiten-Modus) — für Abteilung/bAV … bei „Mensch im Unternehmen". */
  firmaId?: number;
  formFields: Record<string, string | number | boolean>;
  updateField: (name: string, value: string) => void;
  /** Optional: read-only Anzeige BESTEHENDER Verbindungen, oben in der jeweiligen
   *  Karte eingebettet (z. B. in der Altkunden-Bereinigung). In der normalen
   *  Anlage nicht gesetzt → die Karten bleiben reine Neuanlage. */
  existingMenschen?: React.ReactNode;
  existingFirmaConn?: React.ReactNode;
  existingKv?: React.ReactNode;
}) {
  const setKey = (key: string, arr: unknown[]) => updateField(key, JSON.stringify(arr));
  // Die Rechtsform wird im selben Formular erfasst (`legalFormId`), die Karte
  // benennt sich also mit — wer „Eingetragener Verein" wählt, sammelt darunter
  // die „Verantwortung für den Verein".
  const begriff = useUnternehmensBegriff(Number(formFields.legalFormId) || undefined);

  const menschen = parseArr(formFields.rel_menschen) as MenschEntry[];
  const firmaConn = parseArr(formFields.rel_firmaConn) as FirmaConnItem[];

  // Eine FIRMA als Gesellschafterin ist beides: Gesellschafter UND
  // Firmenverbindung. Erfasst wird sie in EINER Karte, angezeigt in beiden
  // (User 28.09.2026) — in der jeweils anderen nur lesend, mit Herkunft.
  const { items: katalog } = useVerantwortlichkeiten();
  const kategorieVon = React.useMemo(() => baueKategorieVon(katalog), [katalog]);
  // Wer Anteile an DIESEM Unternehmen hält — als Mutter oder Gesellschafterin
  // aus den Firmenverbindungen — steht unter „Unternehmensleitung und
  // Gesellschafter", nicht bei den Verbindungen (User 28.09.2026). Die
  // Verbindungen zeigen nur, woran DIESES Unternehmen beteiligt ist. Dieselbe
  // Firma, die dort schon als Gesellschafterin erfasst ist, erscheint nicht
  // ein zweites Mal.
  const gesellschafterIds = new Set(
    menschen
      .filter((m) => m.ref?.type === "Firma" && gruppenVon(m, kategorieVon).has("gesellschafter"))
      .map((m) => m.ref?.contactId)
      .filter((n): n is number => typeof n === "number"),
  );
  const gesellschafterAusVerbindungen: BeziehungsZeile[] = firmaConn
    .filter((it) => it.ref && istFremdeBeteiligung(it))
    .filter((it) => typeof it.ref?.contactId !== "number" || !gesellschafterIds.has(it.ref.contactId))
    .map((it) => ({
      // Ohne Herkunftshinweis (User 28.09.2026) — die Rolle sagt es schon.
      key: `fc-${it.id}`,
      name: refName(it.ref) || "Ohne Namen",
      // Hier steht, was die andere Firma an DIESER hält: Mutterfirma oder eine
      // Beteiligung — „Gesellschafterin" gibt es zwischen Firmen
      // nicht mehr (User 02.10.2026, Paket A4).
      rolle: it.role === MUTTERFIRMA ? MUTTERFIRMA : BETEILIGUNG,
      anteile: eintragAnteile(it) || undefined,
      ...zeilenAnzeigeVon(it.ref),
      // Ziehbar wie die übrigen Zeilen der Firmenakte (A6).
      ziehDaten: ziehDatenVon(firmaQuelle(it, "leitung")),
      ziehMarker: ziehMarkerVon(firmaQuelle(it, "leitung")),
      // Entfernen nimmt die Verbindung aus „Verbindungen zu Firmen" — dort ist
      // sie gespeichert, hier steht sie nur.
      onEntfernen: () => setKey("rel_firmaConn", firmaConn.filter((x) => x.id !== it.id)),
    }));

  // Firmen mit Strukturkante zu dieser Firma: Eine Komplementärin, die dort
  // zugleich steht, ist Holding und bleibt sichtbar (T11).
  const strukturFirmenIds = new Set(
    firmaConn.map((it) => it.ref?.contactId).filter((n): n is number => typeof n === "number"),
  );

  // „+ Verbindung zu Unternehmen" → „Verantwortung fürs Unternehmen (extern)"
  // öffnet das Popup der Verantwortungs-Karte mit einer Firma als Gegenüber (A5).
  const menschenSteuerung = React.useRef<MenschenSteuerung | null>(null);
  const firmaSteuerung = React.useRef<FirmaConnSteuerung | null>(null);

  // ── Ziehen & Ablegen zwischen allen Listen der Firmenakte (Paket A6) ──────
  // Die Regeln stehen in beziehungsZiehen.ts; hier wird nur das passende
  // Popup mit Vorbelegung geöffnet. Abbrechen ändert nichts.
  const ablegen = (ziel: FirmenListe) => (daten: string) => {
    const q = ziehQuelleAus(daten);
    if (!q) return;
    const e = ablageFuer(q, ziel);
    if (e.aktion === "menschBearbeiten") return menschenSteuerung.current?.bearbeiten(q.id, { haken: e.haken });
    if (e.aktion === "menschNeu") {
      const fc = firmaConn.find((x) => x.id === q.id);
      if (fc?.ref) menschenSteuerung.current?.neu({ nurFirma: true, vorlage: { ref: fc.ref } });
      return;
    }
    if (e.aktion !== "firmaConn") return;
    if (q.eintrag === "firmaConn") {
      const fc = firmaConn.find((x) => x.id === q.id);
      if (!fc) return;
      firmaSteuerung.current?.neu(
        e.gruppe,
        e.ersetzen
          ? { ...fc, role: e.role, share: e.share ?? fc.share }
          : { ref: fc.ref, role: e.role, share: "" },
      );
      return;
    }
    const m = menschen.find((x) => x.id === q.id);
    if (m?.ref) firmaSteuerung.current?.neu(e.gruppe, { ref: m.ref, role: e.role, share: "" });
  };
  const ablageFuerListe = (ziel: FirmenListe): TabellenAblage => ({
    art: ZIEH_ART_FIRMENAKTE,
    pruefen: (typen) => pruefeAblage(typen, ziel),
    hinweis: ABLAGE_HINWEIS[ziel],
    onAblage: ablegen(ziel),
  });

  // Organigramm aus den Einträgen der Anlage, Stand beim Öffnen eingefroren.
  const [organigramm, setOrganigramm] = React.useState<AnlageBeziehungen | null>(null);
  const orgKnopf = (
    <DiagrammKnopf
      text="Organigramm"
      onClick={() =>
        setOrganigramm({
          selbstId: firmaId,
          selbstName: str(formFields.name),
          selbstTyp: "Firma",
          menschen,
          firmaConn,
        })
      }
    />
  );

  return (
    <>
      <SectionCard title={verantwortungTitel(begriff)} actions={orgKnopf}>
        {existingMenschen}
        <MenschenFuersUnternehmenList
          begriff={begriff}
          value={menschen}
          onChange={(n) => setKey("rel_menschen", n)}
          legalFormId={zahl(formFields.legalFormId)}
          gesellschafterAusVerbindungen={gesellschafterAusVerbindungen}
          firmaId={firmaId}
          strukturFirmenIds={strukturFirmenIds}
          steuerung={menschenSteuerung}
          ziehen={{
            ablage: ablageFuerListe,
            quelle: (e, liste): ZiehQuelle => ({
              liste,
              eintrag: "mensch",
              id: e.id,
              gegenueber: e.ref?.type === "Firma" ? "Firma" : "Person",
            }),
          }}
          // Telefon/Fax der Firma aus demselben Formular: Zentrale für die
          // Durchwahl der geschäftlichen Nummer einer neu erfassten Person.
          zentrale={[formFields.phone, formFields.fax].map((v) => (typeof v === "string" ? v : ""))}
        />
      </SectionCard>

      <SectionCard title="Verbindungen zu Firmen" actions={orgKnopf}>
        {existingFirmaConn}
        <FirmaConnList
          value={firmaConn}
          onChange={(n) => setKey("rel_firmaConn", n)}
          ausblenden={istFremdeBeteiligung}
          onVerantwortungExtern={() => menschenSteuerung.current?.neu({ nurFirma: true })}
          steuerung={firmaSteuerung}
          ziehen={{ ablage: ablageFuerListe("verbindung"), quelle: (it) => firmaQuelle(it, "verbindung") }}
        />
      </SectionCard>

      <SectionCard title="Kundenverbindung">
        {existingKv}
        <KundenverbindungSelect
          value={str(formFields.rel_kvGroupId)}
          onChange={(v) => updateField("rel_kvGroupId", v)}
        />
      </SectionCard>

      {organigramm && <AnlageOrganigramm daten={organigramm} onClose={() => setOrganigramm(null)} />}
    </>
  );
}
